/**
 * Admin Type Utility Functions
 * Provides secure storage and retrieval of admin type using Base64 encoding
 */

// Simple key for encoding/decoding (in production, use environment variable)
const ENCODE_KEY = 'cc_admin_type_key_2025';

/**
 * Encode admin type using Base64 encoding
 * @param {string} adminType - The admin type to encode
 * @returns {string} - Encoded admin type
 */
const encodeAdminType = (adminType) => {
  if (!adminType) return '';
  
  try {
    // Combine admin type with key and encode
    const combined = `${adminType}:${ENCODE_KEY}`;
    return btoa(combined);
  } catch (error) {
    console.error('Error encoding admin type:', error);
    return '';
  }
};

/**
 * Decode admin type from Base64 encoding
 * @param {string} encodedAdminType - The encoded admin type
 * @returns {string|null} - Decoded admin type or null if invalid
 */
const decodeAdminType = (encodedAdminType) => {
  if (!encodedAdminType) return null;
  
  try {
    const decoded = atob(encodedAdminType);
    const parts = decoded.split(':');
    
    // Verify the key matches
    if (parts.length === 2 && parts[1] === ENCODE_KEY) {
      return parts[0];
    }
    
    return null;
  } catch (error) {
    console.error('Error decoding admin type:', error);
    return null;
  }
};

/**
 * Store admin type securely in storage
 * @param {string} adminType - The admin type to store
 * @param {boolean} remember - Whether to use localStorage (true) or sessionStorage (false)
 */
export const setAdminType = (adminType, remember = false) => {
  const encodedType = encodeAdminType(adminType);
  
  if (remember) {
    localStorage.setItem('cc_at', encodedType);
  } else {
    sessionStorage.setItem('cc_at', encodedType);
  }
};

/**
 * Get admin type from storage and decode it
 * @returns {string|null} - The decoded admin type or null if not found/invalid
 */
export const getAdminType = () => {
  // Try to get from sessionStorage first, then localStorage
  const encodedType = sessionStorage.getItem('cc_at') || localStorage.getItem('cc_at');
  
  if (!encodedType) {
    return null;
  }
  
  return decodeAdminType(encodedType);
};

/**
 * Remove admin type from storage
 */
export const clearAdminType = () => {
  sessionStorage.removeItem('cc_at');
  localStorage.removeItem('cc_at');
  
  // Also clear the old 'type' keys for backward compatibility
  sessionStorage.removeItem('type');
  localStorage.removeItem('type');
};

/**
 * Check if user is super admin
 * @returns {boolean} - True if user is super admin
 */
export const isSuperAdmin = () => {
  const adminType = getAdminType();
  return adminType === 'superadmin';
};

/**
 * Check if user has specific admin type
 * @param {string} type - The admin type to check
 * @returns {boolean} - True if user has the specified admin type
 */
export const hasAdminType = (type) => {
  const adminType = getAdminType();
  return adminType === type;
};
